import React, { forwardRef, useEffect, useMemo, useState } from 'react';

const MAX = 5;

const parseRating = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < 1 || rounded > MAX) return null;
  return rounded;
};

const Rating = forwardRef(
  (
    {
      fieldName,
      label = '',
      value = null,
      disabled = false,
      errors = [],
      allowNull = true,
      style = {},
      pk = null,
      onChange = () => {},
    },
    ref
  ) => {
    const normalized = useMemo(() => parseRating(value), [value]);
    const [hover, setHover] = useState(null);

    const [selected, setSelected] = useState(normalized);
    useEffect(() => {
      setSelected(normalized);
    }, [normalized]);

    const display = hover ?? selected;

    const emit = (next) => {
      setSelected(next);
      if (next === null) {
        onChange({ name: fieldName, type: 'change', value: null, label: '' });
        return;
      }
      onChange({ name: fieldName, type: 'change', value: next, label: String(next) });
    };

    const handlePick = (star) => {
      if (disabled) return;
      if (allowNull && selected === star) {
        emit(null);
        return;
      }
      emit(star);
    };

    const groupId = pk ? `${pk}_${fieldName}` : fieldName;
    const invalid = errors.length > 0;

    return (
      <div
        ref={ref}
        id={groupId}
        className={`rating-field d-flex align-items-center gap-1 ${style.class || ''} ${invalid ? 'is-invalid' : ''}`}
        role="radiogroup"
        aria-label={label || fieldName}
        onMouseLeave={() => setHover(null)}
      >
        {Array.from({ length: MAX }, (_, idx) => {
          const star = idx + 1;
          const active = display != null && star <= display;
          return (
            <button
              key={star}
              type="button"
              className={`btn btn-link p-0 m-0 border-0 lh-1 rating-star ${active ? 'text-warning' : 'text-secondary'}`}
              style={{ fontSize: '1.75rem', lineHeight: 1, textDecoration: 'none' }}
              disabled={disabled}
              role="radio"
              aria-checked={selected === star}
              tabIndex={disabled ? -1 : 0}
              onMouseEnter={() => !disabled && setHover(star)}
              onFocus={() => !disabled && setHover(star)}
              onBlur={() => setHover(null)}
              onClick={() => handlePick(star)}
            >
              {active ? '★' : '☆'}
            </button>
          );
        })}
      </div>
    );
  }
);

Rating.displayName = 'Rating';

export default Rating;
